import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, CreditCard, Check, Loader2, ShieldCheck } from "lucide-react"
import { listPlans, startCheckout } from "@/lib/api"
import type { Plan } from "@/types"

type CreditDialogDetail = {
  message?: string
  minimumMicros?: number
}

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

export default function CreditDialog() {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [minimumMicros, setMinimumMicros] = useState(0)
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(false)
  const [checkoutSku, setCheckoutSku] = useState("")
  const [loadError, setLoadError] = useState("")
  const [token, setToken] = useState("")

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = ((event as CustomEvent).detail || {}) as CreditDialogDetail
      setReason(detail.message || "You need more credit to run this render.")
      setMinimumMicros(Number.isFinite(detail.minimumMicros) ? Math.max(0, Number(detail.minimumMicros)) : 0)
      setOpen(true)
    }
    window.addEventListener("atlas:open-credit-dialog", handler)
    return () => window.removeEventListener("atlas:open-credit-dialog", handler)
  }, [])

  useEffect(() => {
    if (!open) return
    const sessionToken = (window as unknown as Record<string, unknown>).__atlas_token__ as string
    if (sessionToken) setToken(sessionToken)
  }, [open])

  useEffect(() => {
    if (!open || !token) return
    setLoading(true)
    setLoadError("")
    listPlans(token)
      .then((data) => setPlans((data.plans || []).filter((plan) => plan.kind === "credits")))
      .catch(() => {
        setPlans([])
        setLoadError("We couldn't load secure checkout options. Please try again.")
      })
      .finally(() => setLoading(false))
  }, [open, token])

  const availablePlans = useMemo(
    () => plans.filter((plan) => plan.available).sort((a, b) => a.amountCents - b.amountCents),
    [plans],
  )
  const recommendedSku = useMemo(
    () => minimumMicros > 0 ? availablePlans.find((plan) => (plan.creditsMicros || 0) >= minimumMicros)?.sku || "" : "",
    [availablePlans, minimumMicros],
  )

  const handleCheckout = async (sku: string) => {
    if (!token || checkoutSku) return
    setCheckoutSku(sku)
    try {
      const data = await startCheckout(sku, token)
      if (data.checkoutUrl) location.assign(data.checkoutUrl)
      else setCheckoutSku("")
    } catch {
      setCheckoutSku("")
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Checkout could not be started" } }))
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(8, 9, 13, 0.82)" }}
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto surface-elevated p-5 sm:p-7"
            role="dialog"
            aria-modal="true"
            aria-labelledby="credit-dialog-title"
            aria-describedby="credit-dialog-description"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="eyebrow mb-2 text-[#f59e0b]">Production credit</p>
                <h2 id="credit-dialog-title" className="heading-display text-2xl sm:text-3xl">Fund the work before it runs.</h2>
              </div>
              <button aria-label="Close credit options" onClick={() => setOpen(false)} className="p-2 -mr-2 rounded-md hover:bg-[#1e2130] transition-colors">
                <X className="w-5 h-5 text-[#6b7280]" />
              </button>
            </div>
            <p id="credit-dialog-description" className="text-sm text-[#b6bbc8] leading-6 mb-5">{reason} Nothing was charged and no render was started.</p>

            <section className="rounded-xl border border-[#2a2e3f] bg-[#0c0e14] p-4 sm:p-5 mb-5" aria-labelledby="why-credits-title">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#f59e0b]/10 border border-[#f59e0b]/20 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4 text-[#f59e0b]" />
                </div>
                <div>
                  <h3 id="why-credits-title" className="heading-sans text-sm mb-1">Why Atlas uses credits</h3>
                  <p className="text-xs sm:text-sm text-[#8f96a7] leading-5">Video generation uses paid model and GPU time. Credits keep that cost visible and under your control instead of creating a surprise bill after rendering.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-2 mt-4 text-xs text-[#a8adba]">
                <p className="rounded-lg bg-[#12151d] p-3"><Check className="w-3.5 h-3.5 text-[#10b981] mb-2" />See the maximum cost before you approve production.</p>
                <p className="rounded-lg bg-[#12151d] p-3"><Check className="w-3.5 h-3.5 text-[#10b981] mb-2" />Atlas reserves only the approved cap while work runs.</p>
                <p className="rounded-lg bg-[#12151d] p-3"><Check className="w-3.5 h-3.5 text-[#10b981] mb-2" />Any unused reserved credit returns to your balance.</p>
              </div>
            </section>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-[#9ca3af] py-4"><Loader2 className="w-4 h-4 animate-spin" />Loading secure payment options…</div>
            ) : availablePlans.length === 0 ? (
              <div className="rounded-lg border border-[#ef4444]/20 bg-[#ef4444]/5 p-4">
                <p className="text-sm text-[#e5e7eb]">{loadError || "Secure credit checkout is temporarily unavailable."}</p>
                <p className="text-xs text-[#8f96a7] mt-1">Your project and current balance are safe. Please try again shortly.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2 mb-5">
                {availablePlans.map((plan) => (
                  <button
                    key={plan.sku}
                    onClick={() => handleCheckout(plan.sku)}
                    disabled={Boolean(checkoutSku)}
                    className={`relative w-full flex items-center justify-between gap-3 p-4 rounded-xl border bg-[#0f1117] text-left transition-colors disabled:opacity-60 ${recommendedSku === plan.sku ? "border-[#f59e0b]/60 hover:border-[#f59e0b]" : "border-[#25293a] hover:border-[#3b4058]"}`}
                  >
                    <div>
                      {recommendedSku === plan.sku && <span className="inline-block text-[10px] uppercase tracking-[0.14em] text-[#f59e0b] mb-1.5">Covers this production</span>}
                      <b className="block text-base text-[#f0f1f5]">{money.format(plan.amountCents / 100)}</b>
                      <p className="text-xs text-[#8f96a7] mt-0.5">Adds {money.format((plan.creditsMicros || plan.amountCents * 10_000) / 1_000_000)} to your balance</p>
                    </div>
                    {checkoutSku === plan.sku ? <Loader2 className="w-4 h-4 animate-spin text-[#f59e0b]" /> : <CreditCard className="w-4 h-4 text-[#9ca3af]" />}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-5 border-t border-[#202331]">
              <p className="text-xs text-[#737b8e] leading-5">Pay once, not a subscription. Stripe handles your card securely; Atlas never receives the card number.</p>
              <button onClick={() => setOpen(false)} className="btn-subtle shrink-0">Not now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
